import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Quote } from "lucide-react";

interface TestimonialsProps {
  marketSegment?: string;
}

export const Testimonials = ({ marketSegment }: TestimonialsProps) => {
  const { data: testimonials, isLoading } = useQuery({
    queryKey: ["testimonials", marketSegment],
    queryFn: async () => {
      let query = supabase
        .from("testimonials")
        .select("*")
        .eq("approved_for_marketing", true);

      if (marketSegment && marketSegment !== "all") {
        query = query.eq("market_segment", marketSegment);
      }

      const { data, error } = await query.order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="py-12 bg-muted/50">
        <div className="container">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">What Our Clients Say</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-32 bg-muted rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!testimonials || testimonials.length === 0) {
    return null;
  }

  return (
    <div className="py-16 bg-muted/50">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">What Our Clients Say</h2>
          <p className="text-muted-foreground text-lg">
            Hear from compliance officers who trust our platform
          </p>
        </div>

        <Carousel className="mx-auto max-w-5xl">
          <CarouselContent>
            {testimonials.map((testimonial) => (
              <CarouselItem key={testimonial.id} className="md:basis-1/2 lg:basis-1/3">
                <Card className="h-full">
                  <CardContent className="p-6 flex flex-col h-full">
                    <Quote className="h-8 w-8 text-primary mb-4" />
                    <blockquote className="text-sm italic mb-4 flex-1">
                      "{testimonial.quote}"
                    </blockquote>
                    <div className="space-y-2">
                      <div className="font-semibold">{testimonial.contact_name}</div>
                      <div className="text-sm text-muted-foreground">
                        {testimonial.role}
                      </div>
                      <div className="text-sm font-medium">
                        {testimonial.institution_name}
                      </div>
                      {testimonial.market_segment && (
                        <Badge variant="secondary">
                          {testimonial.market_segment.toUpperCase()}
                        </Badge>
                      )}
                      {testimonial.metrics && (
                        <div className="pt-4 border-t space-y-1">
                          {Object.entries(testimonial.metrics as Record<string, any>).map(
                            ([key, value]) => (
                              <div
                                key={key}
                                className="text-xs flex justify-between"
                              >
                                <span className="text-muted-foreground capitalize">
                                  {key.replace(/_/g, " ")}:
                                </span>
                                <span className="font-semibold">{value}</span>
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      </div>
    </div>
  );
};
